import { Button, Col, Form, Input, InputNumber, Row, Select, Skeleton, Tooltip } from "antd";
import { Plug, EyeOff, Eye, Link } from 'lucide-react';
import { ComponentSchemaInputProperties } from "../types";
import { ReactNode, useState } from "react";
import "./index.css";
import type { NamePath } from "antd/es/form/interface";
import type { Rule } from "antd/es/form";
import { CONFIG_CATEGORIES } from "../enums/enums";

interface ComponentConfigFormItemsProps {
    formItems: ComponentSchemaInputProperties;
    required: string[];
    setFieldsValue: (values: any) => void;
    getFieldValue: (name: NamePath) => any;
    validateFields?: (nameList?: NamePath[]) => Promise<any>,
    references: string[];
    handleUpdateReferences: React.Dispatch<React.SetStateAction<string[]>>;
    loading?: boolean;
    emptyText?: ReactNode;
    mappedResourcesOutputs?: { [key: string]: { name: string; suggestionScore: number; }[] };
    filterCategories?: string[];
    onChange?: () => void;
    onReferenceChange?: () => void;
    eksInitialNodeInstanceType?: string;
}

const ComponentConfigFormItems = ({
    formItems,
    required,
    setFieldsValue,
    getFieldValue,
    validateFields,
    references,
    handleUpdateReferences,
    loading,
    emptyText,
    mappedResourcesOutputs,
    onChange,
    onReferenceChange,
    filterCategories,
    eksInitialNodeInstanceType
}: ComponentConfigFormItemsProps) => {
    const [shouldUpdate, setShouldUpdate] = useState<boolean>(false);

    const shouldRenderReferenceButton = mappedResourcesOutputs && Object.values(mappedResourcesOutputs).some(outputs => !!outputs.length);

    const isReferenceMode = (key: string) => references.includes(key);

    const getReferenceButton = (key: string) => {
        const isActive = isReferenceMode(key);
        return (
            <Tooltip
                title={isActive ? "Switch to plain value" : "Reference from another resource"}
            >
                <Button
                    icon={<Link size={14} style={{ 
                        color: isActive ? '#1677ff' : undefined,
                        transition: 'color 0.2s ease'
                    }} />}
                    style={{
                        backgroundColor: isActive ? 'rgba(22, 119, 255, 0.1)' : undefined,
                        borderColor: isActive ? '#1677ff' : undefined,
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                        setFieldsValue?.({ [key]: undefined });
                        isActive ?
                            handleUpdateReferences(references.filter(r => r !== key)) :
                            handleUpdateReferences([...references, key])
                    }}
                />
            </Tooltip>
        );
    };

    const getReferenceIndicator = () => (
        <Tooltip title="This field references another resource's output">
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    marginRight: 6,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(22, 119, 255, 0.1)',
                    color: '#1677ff',
                    cursor: 'help'
                }}
            >
                <Link size={10} />
            </span>
        </Tooltip>
    );

    return loading ? <Skeleton paragraph={{ rows: 1 }} /> :
        Object.keys(formItems).length ?
            <>
                {
                    Object.entries(formItems).sort(([a], [b]) => {
                        // Display the input field 'AppName' on the top
                        if (a === "AppName") { return -1; }     // when 'AppName' is not first in array
                        if (b === "AppName") { return 1; }      // when 'AppName' is first item in array
                        // when both props are required don't change the order
                        // this is done to avoid messing with props order as defined in the templates
                        if (required?.indexOf(b) !== -1 && required?.indexOf(a) !== -1) {
                            return 1;
                        }
                        return required?.indexOf(b) - required?.indexOf(a);
                    })
                        .map(([key, prop]) => {
                            const isRequred = required?.includes(key);
                            const validationRules: Rule[] = [{ required: isRequred, message: `Please input ${key}!` }];

                            if (prop.pattern) {
                                validationRules.push({
                                    pattern: new RegExp(prop.pattern),
                                    message: prop.patternErrorMessage
                                });
                            }

                            // check if resource is eks and formItem is "maxNodes"
                            if (!!eksInitialNodeInstanceType && key === "maxNodes") {
                                validationRules.push(
                                    ({ getFieldValue }) => ({
                                        validator(_rule, newMaxNodesValue) {
                                            const newInstanceTypeValue = getFieldValue("nodeInstanceType");
                                            // if maxNodes is 1 and instance type is changed show validation error
                                            if (newMaxNodesValue === "1" && eksInitialNodeInstanceType !== newInstanceTypeValue) {
                                                return Promise.reject("In order to be able to deploy with no downtime when nodeInstanceType is changed, maxNodes value must be greater than 1. After successful deployment, you can set the maxNodes value back to 1 and redeploy.");
                                            } else {
                                                return Promise.resolve();
                                            }
                                        }
                                    })
                                );
                            }

                            const shouldHideItem = !filterCategories ? CONFIG_CATEGORIES.includes(prop.category!) :
                                filterCategories.includes("all") ? false :
                                    !filterCategories.includes(prop.category!);

                            const isRefMode = isReferenceMode(key);

                            return (
                                <Form.Item
                                    key={key}
                                    name={key}
                                    label={
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            {isRefMode && getReferenceIndicator()}
                                            {prop.label || key}
                                        </span>
                                    }
                                    required={isRequred}
                                    tooltip={prop.description}
                                    rules={validationRules}
                                    className={shouldRenderReferenceButton ? "references" : "no-references"}
                                    hidden={shouldHideItem}
                                >
                                    {
                                        isRefMode && !!mappedResourcesOutputs && Object.keys(mappedResourcesOutputs).length ?
                                            <Input.Group className="flex-justify-space-between">
                                                <Select
                                                    showSearch
                                                    filterOption={(value, option) => option && option.value ? String(option.value).toLowerCase().includes(value.trim().toLowerCase()) : true}
                                                    placeholder={`Select output from existing resource`}
                                                    optionFilterProp="label"
                                                    optionLabelProp="label"
                                                    onChange={(value) => {
                                                        onChange?.();
                                                        setFieldsValue?.({ [key]: value });
                                                        setShouldUpdate(!shouldUpdate);
                                                        // needed to update alert message in DeployResourceModal
                                                        onReferenceChange?.();
                                                    }}
                                                    value={getFieldValue?.(key)}
                                                >
                                                    {
                                                        mappedResourcesOutputs[key].map(({ name, suggestionScore }) => (
                                                            <Select.Option value={name} key={name} label={name}>
                                                                <Row justify="space-between" align="middle">
                                                                    <Col>
                                                                        {name}
                                                                    </Col>
                                                                    <Col>
                                                                        {suggestionScore > 0.65 ?
                                                                            <span style={{
                                                                                fontSize: 11,
                                                                                fontWeight: 500,
                                                                                color: '#8b5cf6',
                                                                                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                                                                padding: '2px 8px',
                                                                                borderRadius: 4,
                                                                                marginLeft: 8
                                                                            }}>
                                                                                Best match
                                                                            </span> :
                                                                            null
                                                                        }
                                                                    </Col>
                                                                </Row>
                                                            </Select.Option>
                                                        ))
                                                    }
                                                </Select>
                                                <Input addonAfter={getReferenceButton(key)} className="hidden-input-use-add-on" />
                                            </Input.Group>
                                            :
                                            prop.enum ?
                                                <Input.Group className="flex-justify-space-between">
                                                    <Select
                                                        showSearch
                                                        mode={prop.type === "array" ? "multiple" : undefined}
                                                        placeholder={`Select ${key}`}
                                                        optionFilterProp="children"
                                                        onChange={async (value) => {
                                                            onChange?.();
                                                            setFieldsValue?.({ [key]: value });
                                                            setShouldUpdate(!shouldUpdate);
                                                            // check if resource is eks and formItem is "nodeInstanceType"
                                                            if (!!eksInitialNodeInstanceType && key === "nodeInstanceType") {
                                                                await validateFields?.(["maxNodes"]);
                                                            }
                                                        }}
                                                        value={getFieldValue?.(key)}
                                                    >
                                                        {
                                                            Object.entries(prop.enum).map(([_, enumValue]) => (
                                                                <Select.Option value={enumValue} key={enumValue}>{enumValue}</Select.Option>
                                                            ))
                                                        }
                                                    </Select>
                                                    {shouldRenderReferenceButton && <Input addonAfter={getReferenceButton(key)} className="hidden-input-use-add-on" />}
                                                </Input.Group>
                                                :
                                                (prop.dataType || prop.type) === "number" ?
                                                    <InputNumber
                                                        onChange={onChange}
                                                        min={prop.minimum}
                                                        max={prop.maximum}
                                                        placeholder={prop.placeholder}
                                                        style={{ width: "100%" }}
                                                        addonAfter={shouldRenderReferenceButton ? getReferenceButton(key) : null}
                                                    /> :
                                                    !prop.sensitive ?
                                                        <Input
                                                            type={"text"}
                                                            onChange={onChange}
                                                            placeholder={prop.placeholder}
                                                            minLength={prop.minLength}
                                                            maxLength={prop.maxLength}
                                                            addonAfter={shouldRenderReferenceButton ? getReferenceButton(key) : null}
                                                        /> :
                                                        <Input.Password
                                                            iconRender={visible => (visible ? <Eye size={14} /> : <EyeOff size={14} />)}
                                                            addonAfter={shouldRenderReferenceButton ? getReferenceButton(key) : null}
                                                            onChange={onChange}
                                                        />
                                    }
                                </Form.Item>
                            )
                        })
                }
            </> :
            <>{emptyText}</>
}

export default ComponentConfigFormItems;